/**
 * Scattered FAA Aircraft Warning Lights
 * 
 * Creates deterministic scattered flashing lights throughout the scene
 * to add ambience and make it feel more lived-in.
 */

import type * as THREE_NS from 'three';

// Deterministic random number generator (seeded)
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    // Linear congruential generator
    this.seed = (this.seed * 1664525 + 1013904223) % 4294967296;
    return this.seed / 4294967296;
  }

  nextFloat(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  nextInt(min: number, max: number): number {
    return Math.floor(this.nextFloat(min, max + 1));
  }
}

export interface ScatteredLightsState {
  lights: THREE_NS.Sprite[];
  lightPhases: number[]; // Unique phase offset for each light
  lightColors: ('red' | 'green')[]; // Color for each light
}

/**
 * Create FAA red obstruction light texture (reusable)
 */
function createFAALightTexture(THREE: typeof THREE_NS, color: 'red' | 'green' = 'red'): THREE_NS.Texture {
  const { CanvasTexture } = THREE as any;
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    if (color === 'green') {
      // FAA green LED color: RGB(0, 255, 0) or similar bright green
      const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      gradient.addColorStop(0, 'rgba(50, 255, 50, 1.0)'); // Bright green-white center
      gradient.addColorStop(0.2, 'rgba(0, 255, 0, 1.0)'); // Pure green
      gradient.addColorStop(0.5, 'rgba(0, 200, 0, 0.9)'); // Darker green
      gradient.addColorStop(0.8, 'rgba(0, 150, 0, 0.5)'); // Fading green
      gradient.addColorStop(1, 'rgba(0, 100, 0, 0)'); // Transparent edge
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);
    } else {
      // FAA red color: RGB(255, 0, 0) or similar bright red
      const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      gradient.addColorStop(0, 'rgba(255, 50, 50, 1.0)'); // Bright red-white center
      gradient.addColorStop(0.2, 'rgba(255, 0, 0, 1.0)'); // Pure red
      gradient.addColorStop(0.5, 'rgba(200, 0, 0, 0.9)'); // Darker red
      gradient.addColorStop(0.8, 'rgba(150, 0, 0, 0.5)'); // Fading red
      gradient.addColorStop(1, 'rgba(100, 0, 0, 0)'); // Transparent edge
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);
    }
  }
  
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Create scattered FAA aircraft warning lights throughout the scene
 */
export function createScatteredLights(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS,
  count: number = 20,
  seed: number = 12345,
  minDistance: number = 300, // Minimum radius - well beyond camera (~25), keep lights far away
  maxDistance: number = 1500 // Maximum distance for immense sense of scale
): ScatteredLightsState {
  const { Sprite, SpriteMaterial } = THREE as any;
  const lights: THREE_NS.Sprite[] = [];
  const lightPhases: number[] = [];
  const lightColors: ('red' | 'green')[] = [];
  
  // Deterministic random generator
  const rng = new SeededRandom(seed);
  
  // FAA flash pattern constants
  const flashRate = 25; // flashes per minute
  const flashCycleDuration = 60.0 / flashRate; // ~2.4 seconds per cycle
  
  // Create textures for both colors (reusable)
  const redTexture = createFAALightTexture(THREE, 'red');
  const greenTexture = createFAALightTexture(THREE, 'green');
  
  for (let i = 0; i < count; i++) {
    // Generate deterministic position in horizontal plane (near Y=0)
    // Use cylindrical coordinates: distance in XZ plane, Y near zero
    const theta = rng.nextFloat(0, Math.PI * 2); // Azimuth around Y-axis (0 to 2π)
    const distance = rng.nextFloat(minDistance, maxDistance);
    
    // Constrain Y to be near zero (same plane as cortex network)
    // Small vertical variation for slight depth, but mostly flat
    const yRange = 5; // ±5 units vertical variation (very small)
    const y = rng.nextFloat(-yRange, yRange);
    
    // Position in XZ plane (horizontal circle around origin)
    const x = distance * Math.cos(theta);
    const z = distance * Math.sin(theta);
    
    // Deterministically assign color (50% green, 50% red for better visibility)
    const colorChoice = rng.nextFloat(0, 1);
    const lightColor: 'red' | 'green' = colorChoice < 0.5 ? 'green' : 'red';
    lightColors.push(lightColor);
    
    // Select appropriate texture
    const lightTexture = lightColor === 'green' ? greenTexture : redTexture;
    
    // Use white color to let the texture color show through properly
    // The texture itself provides the red/green color
    const materialColor = 0xffffff; // White - texture provides the actual color
    
    // Create sprite material
    const material = new SpriteMaterial({
      map: lightTexture,
      color: materialColor,
      transparent: true,
      opacity: 0.15, // Start dim
      blending: (THREE as any).AdditiveBlending,
      depthWrite: false,
    });
    
    const light = new Sprite(material);
    
    // Vary size slightly for visual interest (deterministic)
    const sizeVariation = rng.nextFloat(0.8, 1.2);
    const baseSize = 2.0; // Base size for scattered lights
    light.scale.set(baseSize * sizeVariation, baseSize * sizeVariation, 1);
    
    // Position away from center
    light.position.set(x, y, z);
    
    // Store unique phase offset for each light (staggered flashing)
    const phaseOffset = rng.nextFloat(0, flashCycleDuration);
    lightPhases.push(phaseOffset);
    
    // Store phase, distance, and color in userData for updates
    light.userData.phaseOffset = phaseOffset;
    light.userData.sizeVariation = sizeVariation;
    light.userData.distance = distance; // Store distance for atmospheric perspective
    light.userData.color = lightColor; // Store color for reference
    
    scene.add(light);
    lights.push(light);
  }
  
  return { lights, lightPhases, lightColors };
}

/**
 * Update scattered lights with FAA flash pattern and atmospheric perspective
 * Distant lights have slower, smoother (lerped) flashes to convey scale
 */
export function updateScatteredLights(
  state: ScatteredLightsState,
  currentTime: number,
  minDistance: number = 300,
  maxDistance: number = 1500
): void {
  const baseFlashRate = 25; // flashes per minute (FAA standard) - for close lights
  const baseFlashCycleDuration = 60.0 / baseFlashRate; // ~2.4 seconds per cycle
  const baseFlashOnDuration = 0.15; // Flash ON for 150ms (close lights)
  
  state.lights.forEach((light, index) => {
    const phaseOffset = state.lightPhases[index];
    const distance = light.userData.distance || minDistance;
    
    const material = light.material as THREE_NS.SpriteMaterial;
    const sizeVariation = light.userData.sizeVariation || 1.0;
    const baseSize = 2.0;
    
    // Atmospheric perspective: fade with distance (like Blame! manga)
    // Normalize distance to 0-1 range
    const normalizedDistance = (distance - minDistance) / (maxDistance - minDistance);
    // Exponential falloff for more dramatic fade (distant lights much dimmer)
    const distanceFalloff = Math.pow(1.0 - normalizedDistance, 2.5); // Stronger falloff
    // Clamp to ensure minimum visibility even at max distance
    const minVisibility = 0.1; // Minimum opacity even at max distance
    const maxVisibility = 1.0;
    const visibilityMultiplier = minVisibility + (maxVisibility - minVisibility) * distanceFalloff;
    
    // Slower flash rate for distant lights (conveys scale)
    // Close lights flash faster, distant lights flash much slower
    // Distant lights: 0.2x speed (5x slower), close lights: 1.0x speed
    const flashRateMultiplier = 0.2 + normalizedDistance * 0.8; // 0.2x to 1.0x speed
    const flashCycleDuration = baseFlashCycleDuration / flashRateMultiplier;
    
    const cycleTime = ((currentTime + phaseOffset) % flashCycleDuration);
    
    // Smooth lerped flash pattern (not binary) - creates gradual fade in/out
    // Use sine wave for smooth, continuous pulsing
    // The entire cycle is a smooth sine wave, slower for distant lights
    const flashPhase = (cycleTime / flashCycleDuration) * Math.PI * 2;
    
    // Smooth sine wave pulse: 0 (dim) to 1 (bright) and back
    // Use (sin + 1) / 2 to get 0-1 range, then apply easing for more natural feel
    let rawPulse = (Math.sin(flashPhase) + 1) * 0.5; // 0 to 1
    
    // Apply easing to make it feel more natural (slight ease-in-out)
    const pulseIntensity = rawPulse < 0.5 
      ? 2 * rawPulse * rawPulse // Ease in
      : 1 - 2 * (1 - rawPulse) * (1 - rawPulse); // Ease out
    
    // Lerp between dim and bright states
    const dimOpacity = 0.15;
    const brightOpacity = 2.0;
    const currentOpacity = dimOpacity + (brightOpacity - dimOpacity) * pulseIntensity;
    
    // Apply distance-based visibility
    material.opacity = currentOpacity * visibilityMultiplier;
    
    // Scale also pulses smoothly (slightly larger when brighter)
    const scaleMultiplier = 1.0 + pulseIntensity * 0.2;
    light.scale.set(baseSize * sizeVariation * scaleMultiplier, baseSize * sizeVariation * scaleMultiplier, 1);
  });
}

